import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer } from "@/components/SiteChrome";
import { useAuth } from "@/lib/auth";

export default function Auth() {
    const [mode, setMode] = useState("signin");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const { loginWithPassword, register, loginWithGoogle, user } = useAuth();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const navigate = useNavigate();

    if (user) {
        navigate(next, { replace: true });
    }

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (mode === "signin") await loginWithPassword(email, password);
            else await register(email, password, name);
            toast.success("Welcome");
            navigate(next);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Authentication failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="auth-page">
            <Header />
            <main className="mx-auto max-w-md px-4 py-16 sm:px-6">
                <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Account</div>
                <h1 className="mt-3 font-display text-5xl uppercase italic text-[hsl(var(--ink))]">
                    {mode === "signin" ? "Sign in" : "Create account"}
                </h1>
                <p className="mt-3 text-sm text-[hsl(var(--ink-soft))]">
                    Sign in to save your wishlist, review your orders, and book trail-day slots.
                </p>

                <button
                    onClick={() => loginWithGoogle(next)}
                    className="mt-8 flex w-full items-center justify-center gap-3 border-2 border-[hsl(var(--ink))] bg-white px-4 py-3 font-heading text-sm font-bold uppercase tracking-wider text-[hsl(var(--ink))] hover:bg-neutral-50"
                    data-testid="auth-google"
                >
                    <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Continue with Google
                </button>

                <div className="my-6 flex items-center gap-3">
                    <div className="h-px flex-1 bg-[hsl(var(--dust-300))]" />
                    <span className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">or</span>
                    <div className="h-px flex-1 bg-[hsl(var(--dust-300))]" />
                </div>

                <form onSubmit={submit} className="space-y-3">
                    {mode === "signup" && (
                        <input
                            required
                            type="text"
                            placeholder="Your name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full border border-[hsl(var(--ink))] bg-white px-3 py-3 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                            data-testid="auth-name"
                        />
                    )}
                    <input
                        required
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full border border-[hsl(var(--ink))] bg-white px-3 py-3 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                        data-testid="auth-email"
                    />
                    <input
                        required
                        type="password"
                        minLength={6}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full border border-[hsl(var(--ink))] bg-white px-3 py-3 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                        data-testid="auth-password"
                    />
                    <button
                        type="submit"
                        disabled={busy}
                        className="w-full border-2 border-[hsl(var(--ink))] bg-[hsl(var(--primary))] px-4 py-3 font-heading text-sm font-bold uppercase tracking-wider text-[hsl(var(--ink))] hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60"
                        data-testid="auth-submit"
                    >
                        {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
                    </button>
                </form>

                <button
                    onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
                    className="mt-4 w-full text-sm text-[hsl(var(--ink-soft))] underline underline-offset-4"
                    data-testid="auth-toggle-mode"
                >
                    {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
                </button>
            </main>
            <Footer />
        </div>
    );
}
